import { Card, CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { WorkplaceNameWithDistance } from "@src/appV2/redesign/Shift/Open/WorkplaceNameWithDistance";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type Worker } from "@src/appV2/Worker/api/types";
import { first } from "lodash";

import { getPlacementDetailsPath } from "../../paths";
import { type PlacementData } from "../../types/placement";
import { getCardType } from "../../utils/card-type";
import { PlacementWorkerTypePill } from "../PlacementCard/components/PlacementWorkerTypePill";
import { BookedInterviewCardTitle } from "./components/BookedInterviewCardTitle";
import { SentApplicationCardTitle } from "./components/SentApplicationCardTitle";

interface AppliedPlacementCardProps {
  placement: PlacementData;
  placementCandidate: PlacementCandidate;
  pageIndex: number;
  placementIndex: number;
  source: "booked_interviews" | "sent_applications" | "easy_scheduling_placements";
  worker: Worker;
}

export function AppliedPlacementCard(props: AppliedPlacementCardProps) {
  const { placement, placementCandidate, pageIndex, placementIndex, source, worker } = props;

  const cardType = getCardType(placement);

  const interview = placement?.interviews?.find(
    (interview) =>
      interview.attributes.status === InterviewStatus.BOOKED ||
      interview.attributes.status === InterviewStatus.CANCELLED
  );

  const application = first(placement.applications);
  const appliedDate = application?.attributes.appliedDate;
  const updatedAt = application?.attributes.updatedAt;

  const autoOpenInterviewBooking = source === "easy_scheduling_placements";
  const detailsPath = getPlacementDetailsPath(placement.id, placementCandidate.id, {
    cardType,
    autoOpenInterviewBooking,
  });

  return (
    <Card
      variant="primary"
      sx={{
        border: (theme) => `1px solid ${theme.palette.border?.subtle}`,
        background: (theme) => theme.palette.background.quaternary,
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={detailsPath}
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
            placementCandidateId: placementCandidate.id,
            placementDetails: placement,
            page: pageIndex + 1,
            listIndex: placementIndex + 1,
            source,
            cardType,
            hiringEvents: placement.hiringEvents?.map((event) => {
              return {
                id: event.id,
                name: event.type,
                startDate: event.fixedSchedule?.startDate,
                endDate: event.fixedSchedule?.endDate,
              };
            }),
          });
        }}
      >
        <CardContent>
          <Stack spacing={5} sx={{ paddingTop: 2 }}>
            <Stack spacing={3}>
              {isDefined(interview) ? (
                <BookedInterviewCardTitle interview={interview} workerTimezone={worker.tmz} />
              ) : null}
              {!isDefined(interview) && isDefined(appliedDate) ? (
                <SentApplicationCardTitle
                  appliedDate={appliedDate}
                  workerTimezone={worker.tmz}
                  variant={source === "easy_scheduling_placements" ? "easy_scheduling" : "default"}
                  updatedAt={updatedAt}
                />
              ) : null}
              <WorkplaceNameWithDistance
                textVariant="body1"
                workplaceName={placement.workplace.name}
                formattedDistance={formatDistanceInMiles(placement.distance ?? 0)}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                {isDefined(placement.workerTypes) && (
                  <PlacementWorkerTypePill workerTypes={placement.workerTypes} />
                )}
              </Box>
              {source === "easy_scheduling_placements" && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Text
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.error.main,
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                    })}
                  >
                    Schedule interview now
                  </Text>
                  <CbhIcon
                    type="chevron-right"
                    size="small"
                    sx={(theme) => ({
                      color: theme.palette.error.main,
                    })}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
